import _ from 'lodash';
import { mapGetters } from 'vuex';
import moment from 'moment';
import unitsOfMeasurePlugin from './unitsOfMeasurePlugin';
import { requirePickupBeforeDelivery } from '../types/models/load';

export default {
  computed: {
    ...mapGetters('profiles', {
      isLtnAdmin: 'isLtnAdmin',
    }),
  },
  mixins: [
    unitsOfMeasurePlugin,
  ],
  methods: {
    loadGroupFor(holder) {
      return this.sponsorLoadGroup(holder.loadGroup);
    },
    sponsorLoadGroup(loadGroup) {
      if (loadGroup && !loadGroup.details.sponsor) {
        // eslint-disable-next-line no-param-reassign
        loadGroup.details.sponsor = loadGroup.details.customerCompany || loadGroup.details.listingCompany;
      }
      return loadGroup;
    },
    loadGroupCollectionTruckCount(collection) {
      if (!collection || collection.length === 0) {
        return 0;
      }
      return _.chain(collection)
        .map(g => g.availableLoadCount || _.get(g, 'loadGroup.availableLoadCount', 0))
        .sum()
        .value();
    },
    loadGroupHolderCollectionTruckCount(referenceHolderCollection) {
      if (!referenceHolderCollection || referenceHolderCollection.length === 0) {
        return 0;
      }

      const loadGroupCollection = _.chain(referenceHolderCollection)
        .map(r => this.loadGroupFor(r))
        .value();
      return this.loadGroupCollectionTruckCount(loadGroupCollection);
    },
    loadGroupHoursUntilScheduledPickup(loadGroup) {
      const now = moment();
      const pickup = loadGroup.scheduledPickupTime;
      const momentDate = moment.tz(pickup, loadGroup.details.pickupLocation.googlePlace.timezone);
      return momentDate.diff(now, 'hour');
    },
    loadGroupName(load) {
      return this.loadGroupNickname(load) || this.loadGroupSponsorName(load);
    },
    loadGroupNickname(load) {
      const loadGroup = load.loadGroup || load;
      return _.get(loadGroup, 'details.nickname', null);
    },
    loadGroupRequirements(load) {
      const loadGroup = load.loadGroup || load;
      return _.get(loadGroup, 'details.requirements', {});
    },
    loadGroupRequiresWoodChips(load) {
      return _.get(this.loadGroupRequirements(load), 'requireWoodChips', false);
    },
    loadGroupRequiresWashout(load) {
      return _.get(this.loadGroupRequirements(load), 'requireWashout', false);
    },
    loadGroupRequiresWeatherboards(load) {
      return _.get(this.loadGroupRequirements(load), 'requireWeatherboards', false);
    },
    loadGroupRequiresDeliveryDocument(load) {
      return _.get(this.loadGroupRequirements(load), 'requireDeliveryDocument', null);
    },
    loadGroupRequiresPickupBeforeDelivery(load) {
      return _.get(this.loadGroupRequirements(load), 'requirePickupBeforeDelivery', null);
    },
    loadGroupDispatchInstructions(load) {
      return _.get(this.loadGroupRequirements(load), 'dispatchInstructions', null);
    },
    loadGroupDriverInstructions(load) {
      return _.get(this.loadGroupRequirements(load), 'driverInstructions', null);
    },
    loadGroupSponsorName(load) {
      return this.loadGroupSponsor(load).name;
    },
    loadGroupOwnerName(load) {
      return this.loadGroupOwner(load).name;
    },
    loadGroupOwnerIsSponsor(load) {
      const sponsorId = _.get(this.loadGroupSponsor(load), 'id', null);
      const ownerId = _.get(this.loadGroupOwner(load), 'id');
      return ownerId === sponsorId;
    },
    loadGroupLoadCount(load) {
      const loadGroup = load.loadGroup || load;
      return loadGroup.loadCount;
    },
    loadGroupAvailableLoadCount(load) {
      const loadGroup = load.loadGroup || load;
      return loadGroup.availableLoadCount;
    },
    /*
     * @deprecated
     */
    loadGroupLoadedMiles(load) {
      const loadGroup = load.loadGroup || load;
      return loadGroup.details.loadedMiles;
    },
    loadGroupLoadedDistance(load) {
      const loadGroup = load.loadGroup || load;
      const distance = loadGroup.details.loadedMiles;
      return this.milesToDistanceUnitOfMeasure(distance);
    },
    loadGroupPickupLocation(load) { // TODO: unnecessary: use loadGroupShippingLocation
      const loadGroup = load.loadGroup || load;
      return loadGroup.details.pickupLocation;
    },
    loadGroupDropoffLocation(load) {
      const loadGroup = load.loadGroup || load;
      return loadGroup.details.dropoffLocation;
    },
    loadGroupScheduledPickupTime(load) {
      const loadGroup = load.loadGroup || load;
      return loadGroup.scheduledPickupTime;
    },
    loadGroupOwner(load) {
      const loadGroup = load.loadGroup || load;
      return _.get(loadGroup, 'details.listingCompany');
    },
    loadGroupSponsor(load) {
      const loadGroup = load.loadGroup || load;
      return _.get(this.sponsorLoadGroup(loadGroup), 'details.sponsor');
    },
    loadGroupShipper(load) {
      const loadGroup = load.loadGroup || load;
      return loadGroup.details.pickupCompany;
    },
    loadGroupShippingLocation(load) {
      const loadGroup = load.loadGroup || load;
      return loadGroup.details.pickupLocation;
    },
    loadGroupReceiver(load) {
      const loadGroup = load.loadGroup || load;
      return loadGroup.details.dropoffCompany;
    },
    loadGroupReceivingLocation(load) {
      const loadGroup = load.loadGroup || load;
      return loadGroup.details.dropoffLocation;
    },
    isLoadGroupCustomerVerified(load) {
      const loadGroup = load.loadGroup || load;
      const customerCompany = _.get(loadGroup, 'details.customerCompany');
      if (customerCompany) {
        return this.isLoadGroupCompanyVerified(customerCompany);
      }

      return _.get(loadGroup, 'details.listingCompany.accountStatus.verified', false);
    },
    loadGroupCustomerCompanyName(load) {
      const loadGroup = load.loadGroup || load;
      return _.get(loadGroup, 'details.customerCompany.name', null);
    },
    isLoadGroupCompanyVerified(company) {
      return _.get(company, 'verified');
    },
    // Be advised that load groups don't have trucking companies.. booked loads and transactions do
    loadGroupParticipantsByCompanyId(load) {
      return _.chain([
        this.loadGroupOwner(load),
        this.loadGroupShipper(load),
        this.loadGroupReceiver(load),
        this.loadGroupSponsor(load)],
      )
        .filter(c => !!c)
        .keyBy(c => c.id)
        .value();
    },

    loadGroupPickupLocationGooglePlace(load) {
      return _.get(this.loadGroupPickupLocation(load), 'googlePlace', {}) || {};
    },
    loadGroupPickupLocationChute(load) {
      return _.get(this.loadGroupPickupLocation(load), 'chute', {}) || {};
    },
    loadGroupDropoffLocationGooglePlace(load) {
      return _.get(this.loadGroupDropoffLocation(load), 'googlePlace', {}) || {};
    },
    loadGroupDropoffLocationChute(load) {
      return _.get(this.loadGroupDropoffLocation(load), 'chute', {}) || {};
    },
  },
};



// WEBPACK FOOTER //
// ./src/mixins/loadGroupMixin.js